import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useBusinesses } from "@/hooks/use-businesses";
import { Plus, Sparkles, Pencil, Trash2, Loader2, Zap, AlertTriangle } from "lucide-react";

interface Rule {
  id: number;
  pattern: string;
  ruleType: string;
  conditionJson: any;
  coaAccountId: number;
  priority: number;
  isActive: boolean;
  createdAt: string;
  accountName: string;
  accountCode: string;
  confidence?: number;
  usageCount?: number;
}

interface CoaAccount {
  id: number;
  code: string;
  name: string;
  type: string;
}

interface Suggestion {
  pattern: string;
  coaAccountId: number;
  accountName: string;
  accountCode: string;
  frequency: number;
  confidence: number;
}

function ConfidenceBadge({ value }: { value: number }) {
  if (value >= 95) return <Badge className="bg-green-100 text-green-800">{value}%</Badge>;
  if (value >= 80) return <Badge className="bg-yellow-100 text-yellow-800">{value}%</Badge>;
  return <Badge className="bg-red-100 text-red-800">{value}%</Badge>;
}

export default function RulesPage() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const businessId = activeBusiness?.id;

  const [showBuilder, setShowBuilder] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);

  // Form state
  const [pattern, setPattern] = useState("");
  const [ruleType, setRuleType] = useState("keyword");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [priority, setPriority] = useState("5");
  const [amountOp, setAmountOp] = useState("gt");
  const [amountVal, setAmountVal] = useState("");

  const resetForm = () => {
    setPattern(""); setRuleType("keyword"); setSelectedAccountId(""); setPriority("5"); setAmountOp("gt"); setAmountVal(""); setEditingRule(null);
  };

  // Fetch rules
  const { data: rules = [], isLoading } = useQuery<Rule[]>({
    queryKey: ["/api/rules", businessId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/rules`);
      return res.json();
    },
    enabled: !!businessId,
  });

  // Fetch COA accounts for dropdown
  const { data: coaData } = useQuery<{ accounts: CoaAccount[] }>({
    queryKey: ["/api/businesses", businessId, "chart-of-accounts"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/businesses/${businessId}/chart-of-accounts`);
      return res.json();
    },
    enabled: !!businessId,
  });
  const accounts = coaData?.accounts || [];

  // Fetch suggestions
  const { data: suggestions = [], isLoading: suggestionsLoading, refetch: refetchSuggestions } = useQuery<Suggestion[]>({
    queryKey: ["/api/rules/suggest", businessId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/rules/suggest`);
      return res.json();
    },
    enabled: !!businessId && showSuggestions,
  });

  // Create/update rule
  const saveMutation = useMutation({
    mutationFn: async () => {
      const condJson = ruleType === "amount" ? { [amountOp]: parseFloat(amountVal) } : null;
      const body = {
        pattern, coaAccountId: parseInt(selectedAccountId),
        priority: parseInt(priority), ruleType, conditionJson: condJson,
      };

      if (editingRule) {
        return apiRequest("PATCH", `/api/rules/${editingRule.id}`, body);
      }
      return apiRequest("POST", "/api/rules", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rules"] });
      toast({ title: editingRule ? "Rule updated" : "Rule created successfully" });
      setShowBuilder(false);
      resetForm();
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Toggle active
  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      return apiRequest("PATCH", `/api/rules/${id}`, { isActive });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/rules"] }),
  });

  // Delete
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rules"] });
      toast({ title: "Rule deleted" });
    },
  });

  // Apply all rules
  const applyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/rules/apply", {});
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: `Categorized ${data.categorized} transactions` });
    },
  });

  // Accept suggestion
  const acceptSuggestion = useMutation({
    mutationFn: async (s: Suggestion) => {
      return apiRequest("POST", "/api/rules", {
        pattern: s.pattern, coaAccountId: s.coaAccountId,
        priority: 5, ruleType: "keyword",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rules"] });
      queryClient.invalidateQueries({ queryKey: ["/api/rules/suggest"] });
      toast({ title: "Suggestion accepted as rule" });
    },
  });

  const openEdit = (rule: Rule) => {
    setEditingRule(rule);
    setPattern(rule.pattern);
    setRuleType(rule.ruleType || "keyword");
    setSelectedAccountId(rule.coaAccountId.toString());
    setPriority(rule.priority.toString());
    if (rule.ruleType === "amount" && rule.conditionJson) {
      const key = Object.keys(rule.conditionJson)[0];
      setAmountOp(key || "gt");
      setAmountVal(rule.conditionJson[key]?.toString() || "");
    }
    setShowBuilder(true);
  };

  const priorityLabel = (p: number) => {
    if (p >= 8) return <Badge variant="destructive">High</Badge>;
    if (p >= 4) return <Badge variant="default">Medium</Badge>;
    return <Badge variant="secondary">Low</Badge>;
  };

  const conditionText = (rule: Rule) => {
    const type = rule.ruleType || "keyword";
    if (type === "keyword") return `Description contains "${rule.pattern}"`;
    if (type === "vendor") return `Vendor matches "${rule.pattern}"`;
    if (type === "amount" && rule.conditionJson) {
      const ops: Record<string, string> = { gt: ">", gte: ">=", lt: "<", lte: "<=", eq: "=" };
      const key = Object.keys(rule.conditionJson)[0];
      const val = rule.conditionJson[key];
      const patternPart = rule.pattern ? ` AND contains "${rule.pattern}"` : "";
      return `Amount ${ops[key] || key} $${val}${patternPart}`;
    }
    return rule.pattern;
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Automation Rules</h1>
            <p className="text-muted-foreground text-sm">Auto-categorize transactions using rules. Rules run before AI.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowSuggestions(true); refetchSuggestions(); }} className="gap-2">
              <Sparkles className="h-4 w-4" /> Suggest Rules
            </Button>
            <Button variant="outline" onClick={() => applyMutation.mutate()} disabled={applyMutation.isPending} className="gap-2">
              {applyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Apply Rules
            </Button>
            <Button onClick={() => { resetForm(); setShowBuilder(true); }} className="gap-2">
              <Plus className="h-4 w-4" /> New Rule
            </Button>
          </div>
        </div>

        {/* Rules Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}</div>
            ) : rules.length === 0 ? (
              <div className="p-12 text-center">
                <Zap className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
                <h3 className="text-lg font-medium mb-1">No rules yet</h3>
                <p className="text-muted-foreground text-sm mb-4">Create your first automation rule to auto-categorize transactions.</p>
                <Button onClick={() => { resetForm(); setShowBuilder(true); }} className="gap-2">
                  <Plus className="h-4 w-4" /> Create Rule
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Active</TableHead>
                    <TableHead>Condition</TableHead>
                    <TableHead>Maps To</TableHead>
                    <TableHead className="w-20">Confidence</TableHead>
                    <TableHead className="w-20">Used</TableHead>
                    <TableHead className="w-24">Priority</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id} className={rule.isActive ? "" : "opacity-50"}>
                      <TableCell>
                        <Switch checked={rule.isActive} onCheckedChange={(v) => toggleMutation.mutate({ id: rule.id, isActive: v })} />
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{conditionText(rule)}</div>
                        <Badge variant="outline" className="text-xs mt-1">{rule.ruleType}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{rule.accountCode} — {rule.accountName}</div>
                      </TableCell>
                      <TableCell><ConfidenceBadge value={rule.confidence || 80} /></TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{rule.usageCount || 0}x</span>
                      </TableCell>
                      <TableCell>{priorityLabel(rule.priority)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(rule)}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate(rule.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Rule Builder Modal */}
        <Dialog open={showBuilder} onOpenChange={(v) => { setShowBuilder(v); if (!v) resetForm(); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingRule ? "Edit Rule" : "New Automation Rule"}</DialogTitle>
              <DialogDescription>Define when and how to auto-categorize transactions.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rule Type</Label>
                <Select value={ruleType} onValueChange={setRuleType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keyword">Keyword (description contains)</SelectItem>
                    <SelectItem value="vendor">Vendor (merchant match)</SelectItem>
                    <SelectItem value="amount">Amount (with condition)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{ruleType === "vendor" ? "Vendor Name" : "Keyword / Pattern"}</Label>
                <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder={ruleType === "vendor" ? "e.g. Amazon" : "e.g. office supplies"} />
              </div>

              {ruleType === "amount" && (
                <div className="flex gap-2">
                  <div className="flex-1 space-y-2">
                    <Label>Operator</Label>
                    <Select value={amountOp} onValueChange={setAmountOp}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gt">Greater than</SelectItem>
                        <SelectItem value="gte">Greater or equal</SelectItem>
                        <SelectItem value="lt">Less than</SelectItem>
                        <SelectItem value="lte">Less or equal</SelectItem>
                        <SelectItem value="eq">Equals</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label>Amount ($)</Label>
                    <Input type="number" value={amountVal} onChange={(e) => setAmountVal(e.target.value)} placeholder="100" />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Categorize As (COA Account)</Label>
                <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                  <SelectTrigger><SelectValue placeholder="Select account..." /></SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id.toString()}>{a.code} — {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="9">High (runs first)</SelectItem>
                    <SelectItem value="5">Medium</SelectItem>
                    <SelectItem value="1">Low (runs last)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowBuilder(false); resetForm(); }}>Cancel</Button>
              <Button onClick={() => saveMutation.mutate()} disabled={!pattern || !selectedAccountId || saveMutation.isPending} className="gap-2">
                {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingRule ? "Update Rule" : "Create Rule"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Suggestions Modal */}
        <Dialog open={showSuggestions} onOpenChange={setShowSuggestions}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Suggested Rules</DialogTitle>
              <DialogDescription>Based on your categorized transactions, these patterns could be automated.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {suggestionsLoading ? (
                <div className="p-4 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : suggestions.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                  No suggestions yet. Categorize more transactions first.
                </div>
              ) : (
                suggestions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-medium">"{s.pattern}"</div>
                      <div className="text-xs text-muted-foreground">{s.accountCode} — {s.accountName} ({s.frequency}x, {Math.round(s.confidence * 100)}%)</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => acceptSuggestion.mutate(s)} disabled={acceptSuggestion.isPending}>
                      Accept
                    </Button>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
